/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { ChangeEvent, ChangeEventHandler } from 'react';

import classNames from 'classnames';
import objectPath from 'object-path';

import DotBricksLoading from './DotBricksLoading';
import { Icon } from './Icon';

type ColumnProps<T> = {
  title: string;
  index: string;
  renderer?: (row: T) => void;
};

type ActionProps<T> = {
  title?: string;
  icon: string;
  hidden?: ({ row, index }: { row: T; index: number }) => boolean;
  callback: ({ row, index }: { row: T; index: number }) => void;
};

interface TableProps<T> {
  columns: ColumnProps<T>[];
  data: T[];
  fieldId?: keyof T;
  actions?: ActionProps<T>[];
  showSelection?: boolean;
  onSelect?: (selected: string[]) => void;
  loading?: boolean;
  selected?: string[];
  columnsClassName?: string;
}

export const Table = <T,>({
  columns,
  data,
  fieldId = 'id' as keyof T,
  actions = [],
  showSelection = false,
  loading = false,
  selected = [],
  columnsClassName = '',
  onSelect = () => {},
}: TableProps<T>): JSX.Element => {
  const handleSelectAll: ChangeEventHandler<HTMLInputElement> = (event): void => {
    if (event.target.checked) onSelect(data.map((d) => String(d[fieldId])));
    else onSelect([]);
  };

  const handleSelect = (event: ChangeEvent<HTMLInputElement>, id: string): void => {
    const _selected = [...selected];
    if (event.target.checked && !_selected.includes(id)) _selected.push(id);
    else if (!event.target.checked && _selected.includes(id)) {
      const index = _selected.indexOf(id);
      _selected.splice(index, 1);
    }
    onSelect(_selected);
  };

  return (
    <>
      <table className="min-w-full divide-y divide-white-2.0 flex-grow">
        <thead>
          <tr>
            {showSelection && (
              <th className="data-cell-header rounded-tl">
                <input
                  type="checkbox"
                  checked={selected.length === data.length && data.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
            )}
            {columns.map((column, index) => {
              let className = 'data-cell-header';
              if (index === 0 && !showSelection) className += ' rounded-tl';
              else if (index === columns.length - 1 && !actions.length) className += ' rounded-tr';
              return (
                <th key={column.index} className={className}>
                  {column.title}
                </th>
              );
            })}
            {!!actions.length && <th className="data-cell-header max-w-20 rounded-tr" />}
          </tr>
        </thead>
        {!loading && (
          <tbody className="bg-white.0.0 divide-y divide-white-2.0" data-testid="accounts-list">
            {data.map((item, index) => (
              <tr
                key={item[fieldId] as unknown as string}
                className={index % 2 === 0 ? 'bg-white-0.0' : 'bg-white-1.0'}
              >
                {showSelection && (
                  <td className="data-cell w-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(String(item[fieldId]))}
                      onChange={(e) => handleSelect(e, String(item[fieldId]))}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td className={classNames('data-cell', columnsClassName)} key={column.index}>
                    {typeof column.renderer === 'function'
                      ? column.renderer(item)
                      : objectPath.get(item as any, column.index)}
                  </td>
                ))}
                {!!actions.length && (
                  <td className="data-cell">
                    <div className="flex justify-end">
                      {actions.map((action) => {
                        const onChange =
                          typeof action.callback === 'function' ? action.callback : () => {};
                        let hidden = false;
                        if (typeof action.hidden === 'function')
                          hidden = action.hidden({ row: item, index });
                        if (hidden) return null;
                        return (
                          <button
                            key={action.icon}
                            className="btn bg-white-2.0"
                            title={action.title}
                            onClick={() => onChange({ row: item, index })}
                          >
                            {<Icon name={action.icon} />}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {loading && (
        <div className="mt-20">
          <DotBricksLoading />
        </div>
      )}
    </>
  );
};
